// src/services/openaiService.js - Enhanced with token optimization and visual analysis

class OpenAIService {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.baseURL = 'https://api.openai.com/v1';
  }

  // Count tokens roughly (4 chars ≈ 1 token)
  estimateTokens(text) {
    return Math.ceil(text.length / 4);
  }

  // Truncate content to fit within token limits
  optimizeContentForTokens(content, maxTokens = 50000) {
    const contentStr = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    const estimatedTokens = this.estimateTokens(contentStr);
    
    if (estimatedTokens <= maxTokens) {
      return content;
    }

    console.warn(`Content too large (${estimatedTokens} tokens), optimizing...`);

    // If content is an object with detailed slide data, optimize it
    if (typeof content === 'object' && content.slides) {
      return this.optimizeSlideContent(content, maxTokens);
    }
    
    // If it's a string, truncate it
    const targetChars = maxTokens * 4;
    return contentStr.substring(0, targetChars) + '\n\n[Content truncated due to length...]';
  }

  // Optimize slide content by summarizing detailed slide data
  optimizeSlideContent(content, maxTokens) {
    const optimized = { ...content };
    
    // Keep essential metadata
    // const essentialFields = ['slideCount', 'textContent', 'metadata'];
    
    // Allow much more text content to preserve context for AI
    if (optimized.textContent && optimized.textContent.length > 200000) {
      optimized.textContent = optimized.textContent.substring(0, 200000) + '\n\n[Content truncated...]';
    }

    // Summarize slides data instead of including full detail
    if (optimized.slides && optimized.slides.length > 0) {
      optimized.slidesSummary = {
        totalSlides: optimized.slides.length,
        slidesWithImages: optimized.slides.filter(s => s.images > 0).length,
        slidesWithCharts: optimized.slides.filter(s => s.charts > 0).length,
        slidesWithTables: optimized.slides.filter(s => s.tables > 0).length,
        commonColors: this.extractTopColors(optimized.slides),
        commonFonts: this.extractTopFonts(optimized.slides),
        layoutTypes: this.extractLayoutTypes(optimized.slides),
        averageElementsPerSlide: Math.round(
          optimized.slides.reduce((sum, s) => sum + (s.shapes_count || 0), 0) / optimized.slides.length
        )
      };
      
      // Keep only first 5 slides for detailed analysis
      optimized.slides = optimized.slides.slice(0, 5).map(slide => ({
        slide_number: slide.slide_number,
        text_content: slide.text_content || '',
        layout_type: slide.layout_type,
        images: slide.images || 0,
        charts: slide.charts || 0,
        tables: slide.tables || 0,
        colors: (slide.colors || []).slice(0, 3),
        fonts: (slide.fonts || []).slice(0, 2)
      }));
    }

    return optimized;
  }

  extractTopColors(slides) {
    const colorCount = {};
    slides.forEach(slide => {
      (slide.colors || []).forEach(color => {
        colorCount[color] = (colorCount[color] || 0) + 1;
      });
    });
    
    return Object.entries(colorCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 6)
      .map(([color]) => color);
  }

  extractTopFonts(slides) {
    const fontCount = {};
    slides.forEach(slide => {
      (slide.fonts || []).forEach(font => {
        fontCount[font] = (fontCount[font] || 0) + 1;
      });
    });
    
    return Object.entries(fontCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 4)
      .map(([font]) => font);
  }

  extractLayoutTypes(slides) {
    const layoutCount = {};
    slides.forEach(slide => {
      const layout = slide.layout_type || 'unknown';
      layoutCount[layout] = (layoutCount[layout] || 0) + 1;
    });
    
    return Object.entries(layoutCount)
      .sort(([,a], [,b]) => b - a)
      .map(([layout, count]) => ({ layout, count }));
  }

  async analyzeDesign(content, companyContext = '', audienceData = null) {
    // Optimize content to prevent token limit errors
    const optimizedContent = this.optimizeContentForTokens(content, 50000);
    const optimizedContext = companyContext.length > 1000 ? 
      companyContext.substring(0, 1000) + '\n[Context truncated...]' : companyContext;

    const prompt = this.createDesignCompassPrompt(optimizedContent, optimizedContext, audienceData);
    
    // Final token check - reserve space for completion tokens
    const promptTokens = this.estimateTokens(prompt);
    if (promptTokens > 100000) {  // Leave 28000 tokens for completion + buffer (128k model limit)
      throw new Error(`Prompt still too large (${promptTokens} tokens). Please try with a smaller presentation or contact support.`);
    }

    console.log(`Sending prompt with ~${promptTokens} tokens to OpenAI`);
    
    try {
      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-5-mini',
          messages: [
            {
              role: 'system',
              content: 'You are an expert presentation design consultant specializing in the Design Compass methodology. You analyze presentations and provide strategic design recommendations in JSON format.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_completion_tokens: 64000
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle specific token limit errors with helpful messages
        if (response.status === 429 && errorData.error?.message?.includes('tokens per min')) {
          throw new Error(`OpenAI rate limit exceeded. Your presentation is quite large. Please try again in a few minutes or contact support for enterprise options.`);
        }
        
        throw new Error(`OpenAI API Error: ${response.status} - ${errorData.error?.message || response.statusText}`);
      }

      const data = await response.json();
      const responseText = data.choices[0].message.content;
      
      // Parse JSON response
      try {
        // Clean markdown code blocks if present
        const cleanedResponse = responseText
          .replace(/```json\n?/g, '')
          .replace(/```\n?/g, '')
          .trim();
        
        const analysis = JSON.parse(cleanedResponse);
        
        // Add optimization info to the analysis
        if (content !== optimizedContent) {
          analysis._optimizationInfo = {
            contentOptimized: true,
            originalTokens: this.estimateTokens(JSON.stringify(content)),
            optimizedTokens: this.estimateTokens(JSON.stringify(optimizedContent))
          };
        }
        
        return analysis;
      } catch (parseError) {
        console.error('Failed to parse JSON response:', responseText);
        throw new Error('Invalid response format from analysis service');
      }

    } catch (error) {
      console.error('OpenAI Analysis Error:', error);
      throw error;
    }
  }

  async generateImage(prompt, options = {}) {
    const { size = '1024x1024', quality = 'standard' } = options;
    
    try {
      const response = await fetch(`${this.baseURL}/images/generations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-image-1',
          prompt: prompt,
          size: size,
          n: 1
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`OpenAI Image Generation Error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      
      // Handle gpt-image-1 response format (b64_json instead of url)
      let imageUrl;
      if (data.data[0].b64_json) {
        // Convert base64 to data URL for gpt-image-1
        imageUrl = `data:image/png;base64,${data.data[0].b64_json}`;
      } else if (data.data[0].url) {
        // Use direct URL for DALL-E models
        imageUrl = data.data[0].url;
      } else {
        throw new Error('No image data found in response');
      }
      
      return {
        success: true,
        imageUrl: imageUrl,
        promptUsed: prompt,
        size: size,
        quality: quality,
        revisedPrompt: data.data[0].revised_prompt || prompt
      };

    } catch (error) {
      console.error('OpenAI Image Generation Error:', error);
      throw error;
    }
  }

  createDesignCompassPrompt(content, companyContext = '', audienceData = null) {
    let audienceSection = '';
    if (audienceData) {
      audienceSection = `
AUDIENCE INFORMATION:
- Type: ${audienceData.type}
- Goal: ${audienceData.goal}
- Size: ${audienceData.size}
- Context: ${audienceData.context || 'None provided'}
- Retainer Client: ${audienceData.retainerClient || 'None'}
`;
    }

    // Create visual summary from metadata
    let visualSummary = '';
    if (content.metadata) {
      const meta = content.metadata;
      visualSummary = `
VISUAL ANALYSIS:
- Total Slides: ${content.slideCount || 'Unknown'}
- Colors Detected: ${(meta.colors || []).slice(0, 6).join(', ') || 'None detected'}
- Fonts Detected: ${(meta.fonts || []).slice(0, 4).join(', ') || 'Default fonts'}
- Images: ${meta.total_images || 0} (${meta.has_images ? 'Present' : 'None'})
- Charts: ${meta.total_charts || 0} (${meta.has_charts ? 'Present' : 'None'})
- Design Complexity: ${meta.design_complexity || 'Unknown'}
- Content Density: ${meta.content_density || 'Unknown'}
- CTAs Detected: ${meta.has_ctas ? (meta.ctas_detected || []).slice(0, 3).join('; ') || 'Present but not specified' : 'None found'}`;

      // Add image analysis if available
      if (content.slides) {
        const slidesWithImages = content.slides.filter(slide => slide.image_analysis);
        if (slidesWithImages.length > 0) {
          visualSummary += `\n\nIMAGE CONTENT ANALYSIS:`;
          slidesWithImages.forEach(slide => {
            slide.image_analysis.forEach(img => {
              visualSummary += `\n- Slide ${img.slide_number}: ${img.image_type} (${img.dimensions.width}x${img.dimensions.height}), colors: ${img.dominant_colors.join(', ')}`;
            });
          });
        }
      }
    }

    // Add slides summary if available
    let slidesSummary = '';
    if (content.slidesSummary) {
      const summary = content.slidesSummary;
      slidesSummary = `
SLIDES OVERVIEW:
- Total Slides: ${summary.totalSlides}
- Slides with Images: ${summary.slidesWithImages}
- Slides with Charts: ${summary.slidesWithCharts}
- Common Colors: ${summary.commonColors.join(', ') || 'None'}
- Common Fonts: ${summary.commonFonts.join(', ') || 'None'}
- Average Elements per Slide: ${summary.averageElementsPerSlide}`;
    }

    return `You are a senior presentation design consultant. Analyze this presentation using "The Presentation Design Compass" methodology and provide strategic design recommendations.

${audienceSection}
${visualSummary}
${slidesSummary}

PRESENTATION TEXT CONTENT:
${typeof content === 'object' ? (content.textContent || 'No text content available') : content}

DETAILED SLIDE-BY-SLIDE TEXT CONTENT:
${content.slides ? content.slides.map(slide => `
--- SLIDE ${slide.slide_number} ---
${slide.text_content || 'No text content'}
`).join('\n') : 'No individual slide data available'}

COMPANY CONTEXT:
${companyContext || 'No specific company context provided'}

INSTRUCTIONS:
IMPORTANT: Analyze the extracted TEXT CONTENT above, not slide images. All slide text has been extracted and provided in the "PRESENTATION TEXT CONTENT" and "DETAILED SLIDE-BY-SLIDE TEXT CONTENT" sections. Use this text to understand what's on each slide.

Use "The Presentation Design Compass" framework to analyze this presentation. Consider:
1. Context Understanding - Why does this presentation exist?
2. Ambition Setting - What should it achieve?
3. Design Direction - What visual approach fits best?
4. Storytelling Structure - How should content flow?
5. Execution Guidance - What specific actions to take?

CRITICAL: For executionGuidance, provide SPECIFIC, ACTIONABLE recommendations based on the actual content analyzed. Instead of generic advice like "improve visual consistency," provide targeted recommendations like:
- "Replace the Arial font in slides 2-4 with Montserrat to match slide 1"
- "Reduce the 5 different colors to 3: keep the navy blue, coral accent, and white background"
- "Move the CTA 'Contact us today' from slide 3 to the final slide for better flow"
- "Replace bullet point lists on slides 2 and 5 with icons or visuals"
- "Consolidate the 3 charts on slide 7 into a single comparison chart"

SPECIAL FOCUS ON IMAGES: If image analysis is provided above, give specific recommendations about:
- Image appropriateness for the message (e.g., "The skyline image suggests corporate success, but consider showing people collaborating to emphasize teamwork")
- Image placement and sizing recommendations
- Color harmony between images and presentation theme
- Whether images support or distract from the key message

Base your recommendations on the specific visual analysis data, detected CTAs, colors, fonts, image content, and text content provided above.

Provide your analysis in this exact JSON structure:

{
  "presentationType": {
    "primary": "Executive & Strategic | Sales & Influence | Engagement & Immersion | Informative & Educational",
    "secondary": "Optional secondary type",
    "confidence": "high | medium | low",
    "reasoning": "Explanation of type classification"
  },
  "contextualGrounding": {
    "identifiedObjective": "What this presentation aims to achieve",
    "audienceProfile": "Who the audience is and their needs",
    "stakeholderAlignment": "Assessment of stakeholder buy-in needed"
  },
  "strategicDirection": {
    "primaryStrategy": "Main strategic approach for the presentation",
    "communicationGoal": "Core message to convey",
    "successMetrics": "How success will be measured",
    "riskFactors": ["potential challenges or risks"]
  },
  "designDirection": {
    "colors": {
      "primary": ["#hexcode1", "#hexcode2"],
      "secondary": ["#hexcode3", "#hexcode4"],
      "paletteApproach": "Description of color strategy",
      "colorPsychology": "Why these colors work for this presentation"
    },
    "fonts": {
      "headings": "Recommended heading font",
      "body": "Recommended body font",
      "reasoning": "Why these fonts work"
    },
    "layouts": {
      "recommended": "Layout style recommendation",
      "reasoning": "Why this layout approach works"
    },
    "imagery": {
      "recommended": "Image style recommendation",
      "reasoning": "Why this imagery approach works"
    },
    "backgrounds": {
      "recommended": "Background style recommendation",
      "reasoning": "Why this background approach works"
    }
  },
  "storytellingStructure": {
    "narrativeApproach": "Recommended storytelling method",
    "emotionalTone": "Appropriate emotional approach",
    "keyMessages": ["message 1", "message 2", "message 3"],
    "flowRecommendations": ["flow suggestion 1", "flow suggestion 2"]
  },
  "contentStrategy": {
    "keyPoints": ["point 1", "point 2", "point 3"],
    "callToAction": "Recommended call to action",
    "supportingEvidence": "What evidence/data to include"
  },
  "executionGuidance": {
    "priorityFixes": [
      "Most critical design fix based on the specific slides analyzed",
      "Second most important improvement with specific slide references",
      "Third priority fix with actionable steps"
    ],
    "quickWins": [
      "Easy 5-minute fix with specific instructions (e.g., 'Change font size on slide 3 title from 24pt to 32pt')",
      "Simple color adjustment with exact hex codes or font names",
      "Quick content reorganization with specific slide and element references"
    ],
    "designPrinciples": [
      "Specific principle based on presentation type and audience",
      "Targeted guideline for this content and visual style",
      "Actionable design rule for this specific presentation"
    ],
    "slideTemplateNeeds": [
      "Specific template recommendation based on detected content types",
      "Layout suggestion based on analyzed slide structures"
    ]
  },
  "clientQuestions": {
    "clarifyingQuestions": ["question 1", "question 2", "question 3"],
    "stakeholderQuestions": ["question 1", "question 2", "question 3"],
    "visualReadinessQuestions": ["question 1", "question 2", "question 3"]
  }
}

Respond ONLY with the JSON structure. No additional text or explanation outside the JSON.`;
  }
}

export default OpenAIService;